import React, { useEffect, useState } from "react";
import styled from "styled-components/macro";
import { MdDelete } from "react-icons/md";

import DropZone, { formatFileSize } from "../FileUpload/DropZone";
import Button from "../../Button";
import Spinner from "../../Loaders/Spinner";
import ErrorMessages from "../../Notifications/ErrorMessages";
import { useApi } from "../../../api/useApi";
import useUploadFile from "../../../Hooks/useUploadFile";
import { replaceUploadedFile } from "../../../api/dataSourceMutations";

/**
 * Replaces the file behind an uploaded data source.
 *
 * The source keeps its identity — mapping, policies, run history — and only the
 * file it reads on its next run changes. Uploading is therefore not enough on its
 * own: superseding data someone may be reporting on is a deliberate act, so the
 * replacement is staged first and applied only on confirmation.
 */

// Kept in step with the server, which enforces the same limit independently.
const MAX_UPLOAD_BYTES = 150 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = [".csv", ".xlsx"];

const CurrentFile = styled.div`
  margin-bottom: 1rem;
`;

const FileName = styled.div`
  font-weight: bold;
`;

const UploadedOn = styled.div`
  font-size: 0.8125rem;
  opacity: 0.7;
`;

const StagedFile = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 0;

  button {
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    margin-left: 0.5rem;
  }
`;

const Progress = styled.div`
  margin-top: 0.75rem;
  font-size: 0.9rem;
  opacity: 0.7;
`;

const Actions = styled.div`
  margin-top: 1rem;
  display: flex;
  gap: 0.5rem;
`;

const Confirm = styled.p`
  max-width: 40rem;
`;

const Expired = styled.p`
  max-width: 40rem;
  font-weight: bold;
`;

/**
 * Reads the current file's name and upload time out of the serialized ingress
 * details. Both are absent on a source created before upload times were recorded,
 * which is why neither is assumed to be there.
 */
const readCurrentFile = (details) => {
  if (!details) return {};

  try {
    const parsed = JSON.parse(details);
    return {
      name: parsed?.FileNameWithWildcard,
      uploadedOn: parsed?.UploadedOn,
      expiredOn: parsed?.ExpiredOn,
    };
  } catch {
    return {};
  }
};

const ReplaceUploadedFile = ({ apiData, getUpdatedSource }) => {
  const dataSourceId = apiData?.id;
  const connectionId = apiData?.connection?.id;
  const current = readCurrentFile(apiData?.ingressInfo?.details);

  const [confirming, setConfirming] = useState(false);

  const {
    file,
    setFile,
    upload,
    uploadError,
    progress,
    uploading,
    startUpload,
    reset,
  } = useUploadFile(connectionId);

  const [{ errors: replaceErrors, data: replaced }, doReplace] = useApi();

  useEffect(() => {
    if (replaced) {
      reset();
      setConfirming(false);
      getUpdatedSource();
    }
  }, [replaced, reset, getUpdatedSource]);

  const applyReplacement = () => {
    doReplace({
      query: replaceUploadedFile,
      variables: {
        id: dataSourceId,
        uploadDirectory: upload.uploadDirectory,
        fileName: upload.fileName,
      },
    });
  };

  return (
    <div>
      <h3>{current.expiredOn ? "UPLOAD REQUIRED" : "REPLACE UPLOADED FILE"}</h3>

      {current.expiredOn ? (
        <Expired>
          The uploaded file for this source is no longer available and its data was
          never loaded. Upload the file again to run it.
        </Expired>
      ) : current.name ? (
        <CurrentFile>
          <FileName>{current.name}</FileName>
          <UploadedOn>
            {current.uploadedOn
              ? `Uploaded ${new Date(current.uploadedOn).toLocaleString()}`
              : "Upload date not recorded"}
          </UploadedOn>
        </CurrentFile>
      ) : null}

      {uploadError ? <ErrorMessages errors={uploadError} /> : null}
      {replaceErrors ? <ErrorMessages errors={replaceErrors} /> : null}

      {!file ? (
        <DropZone
          accept={ACCEPTED_EXTENSIONS}
          acceptDescription="csv or xlsx"
          maxBytes={MAX_UPLOAD_BYTES}
          maxDescription="150 MB"
          onFileSelected={setFile}
        />
      ) : (
        <>
          <StagedFile>
            <span>
              {file.name} ({formatFileSize(file.size)})
            </span>
            {!uploading && (
              <button type="button" onClick={reset} title="Remove">
                <MdDelete />
              </button>
            )}
          </StagedFile>

          {!upload ? (
            <>
              {progress !== null ? (
                <Progress>Uploading… {progress}%</Progress>
              ) : null}
              <Actions>
                <Button type="button" onClick={startUpload} disabled={uploading}>
                  {uploading ? <Spinner /> : "Upload"}
                </Button>
                <Button type="button" onClick={reset}>
                  Cancel
                </Button>
              </Actions>
            </>
          ) : !confirming ? (
            <Actions>
              <Button type="button" onClick={() => setConfirming(true)}>
                Replace File
              </Button>
              <Button type="button" onClick={reset}>
                Cancel
              </Button>
            </Actions>
          ) : (
            <>
              <Confirm>
                Replacing the file changes the data this source reports on the next
                time it runs. Its mapping, policies and run history are kept. The
                data already loaded stays in place until you run the report again.
              </Confirm>
              <Actions>
                <Button type="button" onClick={applyReplacement}>
                  Yes, replace it
                </Button>
                <Button type="button" onClick={() => setConfirming(false)}>
                  Cancel
                </Button>
              </Actions>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ReplaceUploadedFile;
